export interface MenuItemType {
  key: string;
  label: string;
  path?: string;
  icon?: string;
  condition?: boolean;
  children?: MenuItemType[];
}

export const getMenuData = (
  t: any,
  userProfile: any,
  moduleAccess: any,
  isAzureAuthenticated: boolean
): MenuItemType[] => [
  {
    key: "dashboard",
    label: t("Dashboard"),
    path: "/dashboard",
    icon: "space_dashboard",
    condition: moduleAccess("default") && userProfile.USER_TYPE !== 3,
  },

  // Immigration
  {
    key: "immigration",
    label: t("Immigration"),
    path: "/immigration/list",
    icon: "passport",
    condition: moduleAccess("immigration"),
  },
  
  {
    key: "tasks",
    label: t("Task"),
    path:
      userProfile.USER_COMPANY !== 1
        ? "/tasks/all-tasks"
        : "/tasks/kanban-view",
    icon: "task",
    condition: moduleAccess("all-tasks"),
  },
  {
    key: "time-sheets",
    label: t("Time sheet"),
    path: "/time-sheets/all",
    icon: "pending_actions",
    condition: moduleAccess("all-time-sheets"),
  },
  {
    key: "invoices",
    label: t("Invoices"),
    path: "/invoices-management/invoices/all",
    icon: "receipt_long",
    condition: moduleAccess("all-invoices"),
  },
  {
    key: "questionnaire",
    label: t("Questionnaire"),
    path: "/questionnaires/all-questionnaires",
    icon: "unknown_document",
    condition: moduleAccess("all-questionnaires"),
  },
  {
    key: "file-system",
    label: t("File system"),
    path: "/file-system",
    icon: "file_present",
    condition: moduleAccess("file-system"),
  },
  {
    key: "email",
    label: t("Email"),
    path: "/email/inbox",
    icon: "mail",
    condition: moduleAccess("email") && isAzureAuthenticated,
  },

  // 🔽 Submenu: Client Onboarding
  {
    key: "client-onboarding",
    label: t("Client Onboarding"),
    icon: "settings_account_box",
    condition: moduleAccess("doc-signature"),
    children: [
      {
        key: "doc-signature",
        label: t("Doc-Signature"),
        path: "/client-onboarding/doc-signature/list",
        icon: "draw",
        condition: true,
      },
      {
        key: "doc-template",
        label: t("Doc-Templates"),
        path: "/client-onboarding/template/list",
        icon: "newspaper",
        condition: userProfile.USER_COMPANY == 1,
      },
    ],
  },

  {
    key: "client-allocation",
    label: t("Client allocation"),
    path: "/client-allocation/internal-users",
    icon: "supervisor_account",
    condition: moduleAccess("client-allocation"),
  },
  {
    key: "cpo",
    label: t("CPO"),
    path: "/cpo/list/all",
    icon: "calculate",
    condition: moduleAccess("all-cpo"),
  },
  {
    key: "split-payroll",
    label: t("Split payroll"),
    path: "/split-payroll/list/all",
    icon: "request_quote",
    condition: moduleAccess("all-split-payroll"),
  },
  {
    key: "masters.company",
    label: t("Company"),
    path: "/masters/company",
    icon: "domain",
    condition: moduleAccess("all-company"),
  },

  // 🔽 Submenu: Users
  {
    key: "users",
    label: t("Users & Access"),
    icon: "manage_accounts",
    condition: moduleAccess("all-users"),
    children: [
      {
        key: "all-users",
        label: t("All users"),
        path: "/users/all-users",
        icon: "group",
        condition: true,
      },
      {
        key: "access-role",
        label: t("Access role"),
        path: "/users/access-role",
        icon: "detection_and_zone",
        condition: moduleAccess("access-role"),
      },
    ],
  },

  {
    key: "news-feed",
    label: t("News Feed"),
    path: "/masters/news-feed",
    icon: "newspaper",
    condition: moduleAccess("news-feed"),
  },
  {
    key: "projects",
    label: t("Projects"),
    path: "/masters/projects",
    icon: "post_add",
    condition: moduleAccess("projects"),
  },
  {
    key: "news-letter",
    label: t("News Letter"),
    path: "/masters/news-letter/list",
    icon: "feed",
    condition: moduleAccess("news-letter"),
  },
  {
    key: "incident",
    label: t("Incident Tracker"),
    path: "/incident/list",
    icon: "bug_report",
    condition: moduleAccess("incident"),
  },
  {
    key: "broadcast",
    label: t("Broadcast"),
    path: "/broadcast/all",
    icon: "campaign",
    condition: moduleAccess("broadcast"),
  },
  {
    key: "banking",
    label: t("Banking"),
    path: "/banking/connections",
    icon: "account_balance",
    condition: moduleAccess("banking"),
  },

  // 🔽 Submenu: Masters
  {
    key: "masters",
    label: t("Masters"),
    icon: "dataset_linked",
    condition: moduleAccess("masters"),
    children: [
      {
        key: "user-function",
        label: t("User function"),
        path: "/masters/user-function",
        icon: "assignment_ind",
        condition: true,
      },
      {
        key: "document-type",
        label: t("Document Type"),
        path: "/masters/document-type",
        icon: "difference",
        condition: true,
      },
      {
        key: "company-category",
        label: t("Company category"),
        path: "/masters/company-category",
        icon: "location_city",
        condition: true,
      },
      {
        key: "company-profile",
        label: t("Company profile"),
        path: "/masters/company-profile",
        icon: "location_city",
        condition: true,
      },
      {
        key: "fees",
        label: t("Fees"),
        path: "/masters/fees",
        icon: "account_balance_wallet",
        condition: true,
      },
      {
        key: "account",
        label: t("Account Master"),
        path: "/masters/account",
        icon: "account_balance",
        condition: true,
      },
      {
        key: "country",
        label: t("Country Master"),
        path: "/masters/country",
        icon: "travel_explore",
        condition: true,
      },
      {
        key: "vat-master",
        label: t("VAT Master"),
        path: "/masters/vat-master",
        icon: "money",
        condition: true,
      },
      {
        key: "vatcat",
        label: t("VAT Category"),
        path: "/masters/vatcat",
        icon: "money",
        condition: true,
      },
      {
        key: "questionnaire-type",
        label: t("Questionnaire type"),
        path: "/masters/questionnaire-type",
        icon: "quiz",
        condition: true,
      },
      {
        key: "questionnaire-status",
        label: t("Questionnaire status"),
        path: "/masters/questionnaire-status",
        icon: "live_help",
        condition: true,
      },
      {
        key: "bank-holiday",
        label: t("Bank Holiday"),
        path: "/masters/bank-holiday",
        icon: "calendar_month",
        condition: true,
      },
      {
        key: "split-payroll-country",
        label: t("Split payroll country"),
        path: "/masters/split-payroll-country",
        icon: "travel_explore",
        condition: true,
      },
      {
        key: "split-payroll-year",
        label: t("Split payroll year"),
        path: "/masters/split-payroll-year",
        icon: "calendar_month",
        condition: true,
      },
      {
        key: "email-template",
        label: t("Email Template"),
        path: "/masters/email-template/list",
        icon: "inbox_customize",
        condition: true,
      },
      {
        key: "email-category",
        label: t("Email Category"),
        path: "/masters/email-category/list",
        icon: "local_post_office",
        condition: true,
      },
      {
        key: "tax-return-type",
        label: t("Tax return type"),
        path: "/masters/tax-return-type/list",
        icon: "request_quote",
        condition: true,
      },
    ],
  },

  // Footer
  {
    key: "user-manual",
    label: t("User manual"),
    path: "/user-manual/list/all",
    icon: "help",
    condition: moduleAccess("all-user-manual"),
  },
];